/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { isDevelopmentEnvironment } from 'src/core/utils/api/environment';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';
import { CustomTextProjectKind } from 'src/customText/core/stores/CustomTextProjectStore';
import { LuisId } from 'src/luis/types/Resource';

const commonRoutes = {
    root() {
        return '/';
    },
    notFoundPage() {
        return '/404';
    },
    notAuthorizedPage() {
        return '/401';
    },
    sessionExpiredPage() {
        return '/session_expired';
    }
};

const luisRoutes = {
    baseUrl() {
        const portal = getPortalFromUrl();

        return portal.kind === 'languageStudio' ? languageStudioRoutes.root().concat('/luis') : '';
    },
    root() {
        return this.baseUrl().concat('/');
    },
    home() {
        return this.baseUrl().concat('/home');
    },
    applicationsPage() {
        return this.baseUrl().concat('/applications');
    },
    applicationPage(appId?: LuisId, versionId?: string) {
        return this.applicationsPage().concat(`/${appId || ':appId'}/versions/${versionId || ':versionId'}`);
    },
    dashboardPage(appId?: LuisId, versionId?: string) {
        return this.applicationPage(appId, versionId).concat('/dashboard');
    },
    buildPage(appId?: LuisId, versionId?: string) {
        return this.applicationPage(appId, versionId).concat('/build');
    },
    managePage(appId?: LuisId, versionId?: string) {
        return this.applicationPage(appId, versionId).concat('/manage');
    },
    intentsPage(appId?: LuisId, versionId?: string) {
        return this.applicationPage(appId, versionId).concat('/build/intents');
    },
    intentPage(appId?: LuisId, versionId?: string, intentId?: LuisId) {
        return this.intentsPage(appId, versionId).concat(`/${intentId || ':intentId'}`);
    },
    entitiesPage(appId?: LuisId, versionId?: string) {
        return this.applicationPage(appId, versionId).concat('/build/entities');
    },
    entityPage(appId?: LuisId, versionId?: string, entityId?: LuisId) {
        return this.entitiesPage(appId, versionId).concat(`/${entityId || ':entityId'}`);
    },
    phraseListsPage(appId?: LuisId, versionId?: string) {
        return this.applicationPage(appId, versionId).concat('/build/phraseLists');
    },
    patternsPage(appId?: LuisId, versionId?: string) {
        return this.applicationPage(appId, versionId).concat('/build/patterns');
    },
    domainsPage(appId?: LuisId, versionId?: string) {
        return this.applicationPage(appId, versionId).concat('/build/domains');
    },
    reviewUtterancesPage(appId?: LuisId, versionId?: string) {
        return this.applicationPage(appId, versionId).concat('/build/review');
    },
    applicationInfoPage(appId?: LuisId, versionId?: string) {
        return this.applicationPage(appId, versionId).concat('/manage/general');
    },
    azureResourcesPage(appId?: LuisId, versionId?: string) {
        return this.applicationPage(appId, versionId).concat('/manage/resources');
    },
    publishSettingsPage(appId?: LuisId, versionId?: string) {
        return this.applicationPage(appId, versionId).concat('/manage/publish');
    },
    versionsPage(appId?: LuisId, versionId?: string) {
        return this.applicationPage(appId, versionId).concat('/manage/versions');
    }
};

const qnaMakerRoutes = {
    root() {
        return languageStudioRoutes.root().concat('/questionAnswering');
    },
    projectsPage() {
        return this.root().concat('/projects');
    },
    azureSearchPage() {
        return this.root().concat('/azureSearch');
    },
    projectPage(projectId?: LuisId) {
        return this.projectsPage().concat(`/${projectId || ':projectId'}`);
    },
    manageSourcesPage(projectName?: LuisId) {
        return this.projectPage(projectName).concat('/manageSources');
    },
    editPage(projectName?: LuisId) {
        return this.projectPage(projectName).concat('/edit');
    },
    detailsPage(projectName?: LuisId, qnaPairId?: string) {
        return this.projectPage(projectName).concat(`/details/${qnaPairId || ':qnaPairId'}`);
    },
    deployPage(projectName?: LuisId) {
        return this.projectPage(projectName).concat('/deploy');
    },
    reviewPage(projectName?: LuisId) {
        return this.projectPage(projectName).concat('/review');
    },
    settingsPage(projectName?: LuisId) {
        return this.projectPage(projectName).concat('/settings');
    }
};

const cluRoutes = {
    root() {
        return languageStudioRoutes.root().concat('/clu');
    },
    projectsPage() {
        return this.root().concat('/projects');
    },
    projectPage(projectName?: LuisId) {
        return this.projectsPage().concat(`/${projectName || ':projectName'}`);
    },
    buildSchemaPage(projectName?: LuisId) {
        return this.projectPage(projectName).concat('/build');
    },
    entityDetailsPage(projectName?: LuisId, entityName?: string) {
        return this.buildSchemaPage(projectName).concat(`/${entityName ?? ':entityName'}`);
    },
    dataTaggingPage(projectName?: LuisId) {
        return this.projectPage(projectName).concat('/tag');
    },
    trainingPage(projectName?: LuisId) {
        return this.projectPage(projectName).concat('/train');
    },
    modelDetailsListPage(projectName?: LuisId) {
        return this.projectPage(projectName).concat('/modelDetails');
    },
    modelDetailsPage(projectName?: LuisId, modelName?: LuisId) {
        return this.modelDetailsListPage(projectName).concat(`/${modelName ?? ':modelName'}`);
    },
    reviewPredictionsPage(projectName?: LuisId) {
        return this.projectPage(projectName).concat('/review');
    },
    deployModelPage(projectName?: LuisId) {
        return this.projectPage(projectName).concat('/deploy');
    },
    testModelsPage(projectName?: LuisId) {
        return this.projectPage(projectName).concat('/test');
    },
    reviewEndpointPage(projectName?: LuisId) {
        return this.projectPage(projectName).concat('/reviewEndpoint');
    },
    projectSettingsPage(projectName?: LuisId) {
        return this.projectPage(projectName).concat('/settings');
    }
};

const orchestrationRoutes = {
    root() {
        return languageStudioRoutes.root().concat('/orchestration');
    },
    projectsPage() {
        return this.root().concat('/projects');
    },
    projectPage(projectName?: LuisId) {
        return this.projectsPage().concat(`/${projectName || ':projectName'}`);
    },
    buildSchemaPage(projectName?: LuisId) {
        return this.projectPage(projectName).concat('/build');
    },
    entityDetailsPage(projectName?: LuisId, entityName?: string) {
        return this.buildSchemaPage(projectName).concat(`/${entityName ?? ':entityName'}`);
    },
    dataTaggingPage(projectName?: LuisId) {
        return this.projectPage(projectName).concat('/tag');
    },
    trainingPage(projectName?: LuisId) {
        return this.projectPage(projectName).concat('/train');
    },
    modelDetailsListPage(projectName?: LuisId) {
        return this.projectPage(projectName).concat('/modelDetails');
    },
    modelDetailsPage(projectName?: LuisId, modelName?: LuisId) {
        return this.modelDetailsListPage(projectName).concat(`/${modelName ?? ':modelName'}`);
    },
    reviewPredictionsPage(projectName?: LuisId) {
        return this.projectPage(projectName).concat('/review');
    },
    deployModelPage(projectName?: LuisId) {
        return this.projectPage(projectName).concat('/deploy');
    },
    testModelsPage(projectName?: LuisId) {
        return this.projectPage(projectName).concat('/test');
    },
    reviewEndpointPage(projectName?: LuisId) {
        return this.projectPage(projectName).concat('/reviewEndpoint');
    },
    projectSettingsPage(projectName?: LuisId) {
        return this.projectPage(projectName).concat('/settings');
    }
};

const customTextRoutes = {
    root() {
        return languageStudioRoutes.root().concat('/customText');
    },
    projectsPage() {
        return this.root().concat('/projects');
    },
    extractionProjectsPage() {
        return this.projectsPage().concat('/extraction');
    },
    classificationProjectsPage() {
        return this.projectsPage().concat('/classification');
    },
    myStoragePage() {
        return this.root().concat('/myStorage');
    },
    extractionProjectPage(projectId?: LuisId) {
        return this.extractionProjectsPage().concat(`/${projectId ?? ':projectId'}`);
    },
    classificationProjectPage(projectId?: LuisId) {
        return this.classificationProjectsPage().concat(`/${projectId ?? ':projectId'}`);
    },
    projectPage(projectId?: LuisId, projectKind: CustomTextProjectKind = 'CustomEntityRecognition') {
        return projectKind === 'CustomEntityRecognition'
            ? this.extractionProjectsPage().concat(`/${projectId ?? ':projectId'}`)
            : this.classificationProjectsPage().concat(`/${projectId ?? ':projectId'}`);
    },
    dataTaggingPage(projectId?: LuisId, modelType?: CustomTextProjectKind) {
        return this.projectPage(projectId, modelType).concat('/tag');
    },
    autoTaggingPage(projectId?: LuisId, modelType?: CustomTextProjectKind) {
        return this.projectPage(projectId, modelType).concat('/autoTag');
    },
    trainingPage(projectId?: LuisId, modelType?: CustomTextProjectKind) {
        return this.projectPage(projectId, modelType).concat('/train');
    },
    modelDetailsListPage(projectId?: LuisId, modelType?: CustomTextProjectKind) {
        return this.projectPage(projectId, modelType).concat('/modelDetails');
    },
    modelDetailsPage(projectId?: LuisId, modelType?: CustomTextProjectKind, modelName?: LuisId) {
        return this.modelDetailsListPage(projectId, modelType).concat(`/${modelName ?? ':modelName'}`);
    },
    deployModelPage(projectId?: LuisId, modelType?: CustomTextProjectKind) {
        return this.projectPage(projectId, modelType).concat('/deploy');
    },
    testModelsPage(projectId?: LuisId, modelType?: CustomTextProjectKind) {
        return this.projectPage(projectId, modelType).concat('/test');
    },
    projectSettingsPage(projectId?: LuisId, modelType?: CustomTextProjectKind) {
        return this.projectPage(projectId, modelType).concat('/settings');
    }
};

const translationRoutes = {
    root() {
        return languageStudioRoutes.root().concat('/translation');
    },
    documentTranslationHistory() {
        return this.root().concat('/history');
    },
    documentTranslationSettings() {
        return this.root().concat('/settings');
    }
};

const demosRoutes = {
    root() {
        return '/demos';
    },
    chatGptExpedia() {
        return this.root().concat('/chatGptExpedia');
    }
};

const languageStudioRoutes = {
    root() {
        return isDevelopmentEnvironment() ? '/languageStudio' : '';
    },
    home() {
        return this.root().concat('/home');
    },
    projects() {
        return this.root().concat('/projects');
    },
    demos() {
        return this.root().concat('/tryout');
    },
    namedEntitiesDemoPage() {
        return this.demos().concat('/namedEntities');
    },
    sentimentDemoPage() {
        return this.demos().concat('/sentiment');
    },
    detectLanguageDemoPage() {
        return this.demos().concat('/detectLanguage');
    },
    questionAnswersDemoPage() {
        return this.demos().concat('/answerQuestions');
    },
    generateQuestionsDemoPage() {
        return this.demos().concat('/generateQuestions');
    },
    piiDemoPage() {
        return this.demos().concat('/pii');
    },
    keyPhrasesDemoPage() {
        return this.demos().concat('/keyPhrases');
    },
    linkedEntitiesDemoPage() {
        return this.demos().concat('/linkedEntities');
    },
    textTranslationDemoPage() {
        return this.demos().concat('/textTranslation');
    },
    documentTranslationDemoPage() {
        return this.demos().concat('/documentTranslation');
    },
    taForHealthDemoPage() {
        return this.demos().concat('/healthAnalysis');
    },
    summarizationDemoPage() {
        return this.demos().concat('/summarization');
    },
    callCenterDemoPage() {
        return this.demos().concat('/callCenter');
    }
};

const summarizationRoutes = {
    root() {
        return languageStudioRoutes.root().concat('/summarization');
    },
    projectsPage() {
        return this.root().concat('/projects');
    },
    projectPage(projectId?: LuisId) {
        return this.projectsPage().concat(`/${projectId ?? ':projectId'}`);
    },
    dataTaggingPage(projectId?: LuisId) {
        return this.projectPage(projectId).concat('/tag');
    },
    autoTaggingPage(projectId?: LuisId) {
        return this.projectPage(projectId).concat('/autoTag');
    },
    trainingPage(projectId?: LuisId) {
        return this.projectPage(projectId).concat('/train');
    },
    modelDetailsListPage(projectId?: LuisId) {
        return this.projectPage(projectId).concat('/modelDetails');
    },
    modelDetailsPage(projectId?: LuisId, modelName?: LuisId) {
        return this.modelDetailsListPage(projectId).concat(`/${modelName ?? ':modelName'}`);
    },
    deployModelPage(projectId?: LuisId) {
        return this.projectPage(projectId).concat('/deploy');
    },
    testModelsPage(projectId?: LuisId) {
        return this.projectPage(projectId).concat('/test');
    },
    projectSettingsPage(projectId?: LuisId) {
        return this.projectPage(projectId).concat('/settings');
    }
};

const customHealthcareRoutes = {
    root() {
        return languageStudioRoutes.root().concat('/customHealthcare');
    },
    projectsPage() {
        return this.root().concat('/projects');
    },
    projectPage(projectId?: LuisId) {
        return this.projectsPage().concat(`/${projectId ?? ':projectId'}`);
    },
    buildSchemaPage(projectName?: LuisId) {
        return this.projectPage(projectName).concat('/build');
    },
    entityDetailsPage(projectName?: LuisId, entityName?: string) {
        return this.buildSchemaPage(projectName).concat(`/${entityName ?? ':entityName'}`);
    },
    dataTaggingPage(projectId?: LuisId) {
        return this.projectPage(projectId).concat('/tag');
    },
    autoTaggingPage(projectId?: LuisId) {
        return this.projectPage(projectId).concat('/autoTag');
    },
    trainingPage(projectId?: LuisId) {
        return this.projectPage(projectId).concat('/train');
    },
    modelDetailsListPage(projectId?: LuisId) {
        return this.projectPage(projectId).concat('/modelDetails');
    },
    modelDetailsPage(projectId?: LuisId, modelName?: LuisId) {
        return this.modelDetailsListPage(projectId).concat(`/${modelName ?? ':modelName'}`);
    },
    deployModelPage(projectId?: LuisId) {
        return this.projectPage(projectId).concat('/deploy');
    },
    testModelsPage(projectId?: LuisId) {
        return this.projectPage(projectId).concat('/test');
    },
    projectSettingsPage(projectId?: LuisId) {
        return this.projectPage(projectId).concat('/settings');
    }
};

const customSentimentRoutes = {
    root() {
        return languageStudioRoutes.root().concat('/customSentiment');
    },
    projectsPage() {
        return this.root().concat('/projects');
    },
    projectPage(projectId?: LuisId) {
        return this.projectsPage().concat(`/${projectId ?? ':projectId'}`);
    },

    dataTaggingPage(projectId?: LuisId) {
        return this.projectPage(projectId).concat('/tag');
    },
    autoTaggingPage(projectId?: LuisId) {
        return this.projectPage(projectId).concat('/autoTag');
    },
    trainingPage(projectId?: LuisId) {
        return this.projectPage(projectId).concat('/train');
    },
    modelDetailsListPage(projectId?: LuisId) {
        return this.projectPage(projectId).concat('/modelDetails');
    },
    modelDetailsPage(projectId?: LuisId, modelName?: LuisId) {
        return this.modelDetailsListPage(projectId).concat(`/${modelName ?? ':modelName'}`);
    },
    deployModelPage(projectId?: LuisId) {
        return this.projectPage(projectId).concat('/deploy');
    },
    testModelsPage(projectId?: LuisId) {
        return this.projectPage(projectId).concat('/test');
    },
    projectSettingsPage(projectId?: LuisId) {
        return this.projectPage(projectId).concat('/settings');
    }
};

/**
 * Returns an object with functions that generate the urls
 * that describe the current pages in the portal.
 */
export const routeTable = {
    clu: cluRoutes,
    ...commonRoutes,
    luis: luisRoutes,
    qnaMaker: qnaMakerRoutes,
    customText: customTextRoutes,
    customHealthcare: customHealthcareRoutes,
    customSentiment: customSentimentRoutes,
    summarization: summarizationRoutes,
    orchestration: orchestrationRoutes,
    translation: translationRoutes,
    languageStudio: languageStudioRoutes,
    demos: demosRoutes
};
